import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DateRangePicker } from "rsuite";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAPI } from "../Hooks/useAPIs";
import { useNotification } from '../common/NotificationContext';
import "rsuite/dist/rsuite.min.css";
import { formatISO, format } from "date-fns";
import {
  Box,
  Button,
  Alert as MuiAlert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Checkbox,
  Divider,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentIcon from "@mui/icons-material/Assignment";

const MAX_FILE_SIZE = 3.95 * 1024 * 1024 * 1024;

const COMPONENT_STYLES = {
  mainContainer: { height: '95vh', display: "flex", bgcolor: "background.default", overflowY: "hidden", overflowX: "auto" },
  sidePanel: { borderRadius: 0, borderRight: 1, borderColor: "divider", width: "350px", overflow: "auto", maxHeight: "100%", p: 2 },
  content: { px: 1, py: 1, flex: 1, overflow: "auto", minHeight: '95vh' },
  loadingContainer: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" },
};

export default function DBcombiner() {
  const [range, setRange] = useState([null, null]);
  const [url, setUrl] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});
  const { apiCall } = useAPI();
  const queryClient = useQueryClient();
  const notify = useNotification();
  const navigate = useNavigate();

  const startISO = range[0] ? formatISO(range[0]) : null;
  const endISO = range[1] ? formatISO(range[1]) : null;

  useEffect(() => {
    if (startISO && endISO) {
      const initialUrl = `/search_wivi?start_date=${encodeURIComponent(
        startISO
      )}&end_date=${encodeURIComponent(endISO)}`;
      setUrl(initialUrl);
    } else {
      setUrl(null);
    }
  }, [startISO, endISO]);

  const {
    data: response,
    error,
    isFetching,
    isFetched,
  } = useQuery({
    queryKey: ["vehicleData", url],
    queryFn: async () => {
      if (!url) return null;
      const data = await apiCall("get", url);
      return data.data;
    },
    enabled: !!url,
    keepPreviousData: true,
  });

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const calculateTotalSelectedSize = () => {
    if (!response?.vehicle_data) return 0;
    
    let totalSize = 0;
    Object.entries(response.vehicle_data).forEach(([vin, items]) => {
      items.forEach((item, index) => {
        const key = `${vin}-${item.versionId}-${index}`;
        if (checkedItems[key]) {
          totalSize += item.size;
        }
      });
    });
    return totalSize;
  };

  const processAllMutation = useMutation({
    mutationFn: async ({ versionData, vin }) => {
      const totalSize = versionData.reduce((sum, item) => sum + item.size, 0);
      if (totalSize > MAX_FILE_SIZE) {
        throw new Error(`Total file size exceeds ${formatFileSize(MAX_FILE_SIZE)} limit. Please select smaller files.`);
      }
      return apiCall("post", "/process_bulk", { versionData, vin });
    },
    onSuccess: (data) => {
      let message = data.message || "Bulk process started successfully!";
      if (data.warning) {
        message += `\n${data.warning}`;
      }
      notify({ message, severity: 'success' });
    },
    onError: (err) => {
      const message = err.response?.data?.message || "Failed to process bulk";
      const rejected = err.response?.data?.rejected_version_ids;
      if (rejected && rejected.length > 0) {
        notify({ message: `${message}: ${rejected.join(", ")}`, severity: 'error' });
      } else {
        notify({ message, severity: 'error' });
      }
    },
  });

  const handleCheckboxChange = (itemKey, isChecked) => {
    setCheckedItems((prev) => ({
      ...prev,
      [itemKey]: isChecked,
    }));
  };

  const handleSelectAllForVin = (vin, items, isChecked) => {
    const updated = { ...checkedItems };
    items.forEach((item, index) => {
      const key = `${vin}-${item.versionId}-${index}`;
      updated[key] = isChecked;
    });
    setCheckedItems(updated);
  };

  const handleClearFilters = () => {
    setRange([null, null]);
    setUrl(null);
    setCheckedItems({});
    queryClient.invalidateQueries({ queryKey: ["vehicleData"] });
  };

  const handleNext = async () => {
    if (response?.pagination?.next_page) {
      const nextUrl = response.pagination.next_page;
      setUrl(nextUrl);
      await queryClient.invalidateQueries({ queryKey: ["vehicleData", nextUrl] });
    }
  };

  const handlePrev = async () => {
    if (response?.pagination?.prev_page) {
      const prevUrl = response.pagination.prev_page;
      setUrl(prevUrl);
      await queryClient.invalidateQueries({ queryKey: ["vehicleData", prevUrl] });
    }
  };

  const renderGroupedTable = (vin, items) => {
    const checkedItemsForVin = items.filter((item, index) => {
      const key = `${vin}-${item.versionId}-${index}`;
      return checkedItems[key];
    });

    const allSelected = checkedItemsForVin.length === items.length;
    const totalSizeForVin = checkedItemsForVin.reduce((sum, item) => sum + item.size, 0);
    const isOverSizeLimit = totalSizeForVin > MAX_FILE_SIZE;

    const handleProcessAllForVin = (e) => {
      e.stopPropagation();
      if (checkedItemsForVin.length === 0) {
        notify({ message: 'Please select at least one item to process.', severity: 'error' });
        return;
      }
      if (isOverSizeLimit) {
        notify({ message: `Total size of selected items (${formatFileSize(totalSizeForVin)}) exceeds ${formatFileSize(MAX_FILE_SIZE)} limit. Please unselect some items to proceed.`, severity: 'error' });
        return;
      }
      const versionData = checkedItemsForVin.map((item) => ({
        versionId: item.versionId,
        size: item.size,
      }));
      processAllMutation.mutate({ versionData, vin });
    };

    return (
      <Accordion key={vin}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'action.hover', borderBottom: '1px solid', borderBottomColor: 'divider' }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                VIN: {vin}
              </Typography>
              <Chip label={`${items.length} record${items.length > 1 ? "s" : ""}`} size="small" color="primary" />
              <Chip label={`${checkedItemsForVin.length} selected`} size="small" color="secondary" />
              <Chip 
                label={`Total: ${formatFileSize(totalSizeForVin)}`} 
                size="small" 
                color={isOverSizeLimit ? "error" : "default"}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={allSelected}
                indeterminate={checkedItemsForVin.length > 0 && !allSelected}
                onChange={(e) => handleSelectAllForVin(vin, items, e.target.checked)}
              />
            </Box>
          </Box>
        </AccordionSummary>
        
        <Box sx={{ px: 2, py: 1, bgcolor: "action.hover", borderBottom: "1px solid", borderBottomColor: "divider" }}>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={handleProcessAllForVin}
            disabled={processAllMutation.isLoading || checkedItemsForVin.length === 0 || isOverSizeLimit}
          >
            {processAllMutation.isLoading ? "Processing..." : "Process All Selected"}
          </Button>
        </Box>
        
        <AccordionDetails sx={{ p: 0 }}>
          <TableContainer>
            <Table sx={{ width: "100%", tableLayout: "fixed" }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell padding="checkbox" sx={{ width: "5%" }}><strong>Select</strong></TableCell>
                  <TableCell sx={{ width: "10%" }}><strong>Version ID</strong></TableCell>
                  <TableCell sx={{ width: "60%" }}><strong>File Name</strong></TableCell>
                  <TableCell sx={{ width: "10%" }}><strong>Start Date</strong></TableCell>
                  <TableCell sx={{ width: "10%" }}><strong>End Date</strong></TableCell>
                  <TableCell sx={{ width: "10%" }}><strong>Size</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => {
                  const itemKey = `${vin}-${item.versionId}-${index}`;
                  const isChecked = checkedItems[itemKey] || false;
                  return (
                    <TableRow key={itemKey} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) => handleCheckboxChange(itemKey, e.target.checked)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.versionId}</TableCell>
                      <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.fileName}</TableCell>
                      <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.startDate ? format(new Date(item.startDate), "yyyy/MM/dd HH:mm") : "-"}</TableCell>
                      <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.endDate ? format(new Date(item.endDate), "yyyy/MM/dd HH:mm") : "-"}</TableCell>
                      <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{formatFileSize(item.size)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  };

  const totalRecords = response?.vehicle_data
    ? Object.values(response.vehicle_data).reduce((sum, items) => sum + items.length, 0)
    : 0;

  return (
    <Box sx={COMPONENT_STYLES.mainContainer}>
      <Paper sx={COMPONENT_STYLES.sidePanel}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Filters
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "medium" }}>
          Date Range
        </Typography>
        <DateRangePicker
          format="yyyy-MM-dd HH:mm:ss"
          style={{ width: "100%", paddingBottom: "15px", paddingTop: "5px" }}
          placeholder="Select date range"
          value={range}
          onChange={(value) =>
            setRange(!value || (Array.isArray(value) && value.length === 0) ? [null, null] : value)
          }
        />
        {error && (
          <MuiAlert severity="error" sx={{ mt: 2 }}>
            {error.message || "Failed to fetch data"}
          </MuiAlert>
        )}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 2 }}
          startIcon={<ClearIcon />}
          onClick={handleClearFilters}
        >
          Clear Filters
        </Button>
        <Button
          variant="outlined"
          fullWidth
          sx={{ mt: 2, borderColor: "primary.main", color: "primary.main" }}
          startIcon={<AssignmentIcon />}
          onClick={() => navigate("/dbcombiner_task_status")}
        >
          Task Status
        </Button>
      </Paper>
      
      <Box sx={COMPONENT_STYLES.content}>
        {(() => {
          const totalSelectedSize = calculateTotalSelectedSize();
          const isOverLimit = totalSelectedSize > MAX_FILE_SIZE;
          const hasSelectedItems = totalSelectedSize > 0;
          
          return (
            <>
              {hasSelectedItems && (
                <Box sx={{ mb: 2 }}>
                  <MuiAlert 
                    severity={isOverLimit ? "error" : "info"}
                    sx={{ 
                      mb: 1,
                      border: isOverLimit ? "1px solid #f44336" : "1px solid #2196f3"
                    }}
                  >
                    <Typography variant="body2">
                      <strong>Selected Files Total Size:</strong> {formatFileSize(totalSelectedSize)}
                      <br />
                      <strong>Maximum Allowed Size:</strong> {formatFileSize(MAX_FILE_SIZE)}
                      {isOverLimit && (
                        <>
                          <br />
                          <strong style={{ color: "#f44336" }}>
                            ⚠️ Total size exceeds limit by {formatFileSize(totalSelectedSize - MAX_FILE_SIZE)}. 
                            Please unselect some files to proceed.
                          </strong>
                        </>
                      )}
                    </Typography>
                  </MuiAlert>
                </Box>
              )}
              
              {isFetching ? (
                <Box sx={COMPONENT_STYLES.loadingContainer}>
                  <CircularProgress size={40} />
                </Box>
              ) : isFetched && response?.vehicle_data && Object.keys(response.vehicle_data).length > 0 ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Found {Object.keys(response.vehicle_data).length} unique VIN(s) with {totalRecords} total records
                  </Typography>
                  <Box sx={{ maxHeight: "calc(100vh - 250px)", overflow: "auto", width: "100%" }}>
                    {Object.entries(response.vehicle_data).sort(([a], [b]) => a.localeCompare(b)).map(([vin, items]) => renderGroupedTable(vin, items))}
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, position: "sticky", bottom: 0, bgcolor: "background.paper", py: 2, borderTop: "1px solid", borderTopColor: "divider" }}>
                    <Button variant="outlined" onClick={handlePrev} disabled={!response.pagination?.prev_page || isFetching}>
                      Previous
                    </Button>
                    <Button variant="outlined" onClick={handleNext} disabled={!response.pagination?.next_page || isFetching}>
                      Next
                    </Button>
                  </Box>
                </>
              ) : isFetched && (!response?.vehicle_data || Object.keys(response.vehicle_data).length === 0) ? (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  No data found for the selected range.
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a date range to view results.
                </Typography>
              )}
            </>
          );
        })()}
        
      </Box>
    </Box>
  );
}